/*
 * Dargano theme behaviour, loaded with `defer` from layout/theme.liquid.
 * Plain JavaScript, no dependencies. It does what the React landing page did:
 * the header's scrolled state, the FAQ accordion and the contact form.
 * Sections reloaded in the theme editor are re-initialised.
 */
(() => {
  'use strict';

  /* Header: a hairline appears once the page has scrolled ----------------- */
  const syncHeader = () => {
    const scrolled = String(window.scrollY > 8);
    document.querySelectorAll('.header').forEach((header) => {
      header.setAttribute('data-scrolled', scrolled);
    });
  };
  window.addEventListener('scroll', syncHeader, { passive: true });

  /* FAQ: one answer open at a time; clicking the open one closes it -------- */
  const setOpen = (button, open) => {
    button.setAttribute('aria-expanded', String(open));
    const answer = document.getElementById(button.getAttribute('aria-controls'));
    if (answer) answer.setAttribute('data-open', String(open));
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.faq__q');
    if (!button) return;
    const wasOpen = button.getAttribute('aria-expanded') === 'true';
    button
      .closest('.faq')
      .querySelectorAll('.faq__q')
      .forEach((other) => setOpen(other, false));
    if (!wasOpen) setOpen(button, true);
  });

  /* Contact form ----------------------------------------------------------- */
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const FIELDS = ['name', 'email', 'topic', 'message'];

  const initContactForm = (form) => {
    if (form.dataset.ready) return;
    form.dataset.ready = 'true';

    const t = JSON.parse(form.dataset.messages);
    const { endpoint, key } = form.dataset;
    const sentTemplate = document.getElementById(`${form.id}-sent`);
    const submit = form.querySelector('button[type="submit"]');
    const count = form.querySelector('.field__count');
    const input = (name) => form.elements.namedItem(name);

    const updateCount = () => {
      const message = input('message');
      count.textContent = `${message.value.length} / ${message.maxLength}`;
    };

    // A field's hint (data-hint) stays in aria-describedby; the error is added to it.
    const describe = (el, ...ids) => {
      const value = [el.dataset.hint, ...ids].filter(Boolean).join(' ');
      if (value) el.setAttribute('aria-describedby', value);
      else el.removeAttribute('aria-describedby');
    };

    const setError = (name, text) => {
      const el = input(name);
      const id = `${el.id}-error`;
      let error = form.querySelector(`[id="${id}"]`);
      if (!text) {
        if (error) error.remove();
        el.removeAttribute('aria-invalid');
        describe(el);
        return;
      }
      if (!error) {
        error = document.createElement('p');
        error.className = 'field__error';
        error.id = id;
        el.closest('.field').append(error);
      }
      error.textContent = text;
      el.setAttribute('aria-invalid', 'true');
      describe(el, id);
    };

    const setAlert = (text) => {
      let alert = form.querySelector('.form__alert');
      if (!text) {
        if (alert) alert.remove();
        return;
      }
      if (!alert) {
        alert = document.createElement('p');
        alert.className = 'form__alert';
        alert.setAttribute('role', 'alert');
        submit.before(alert);
      }
      alert.textContent = text;
    };

    const validate = () => {
      const value = (name) => input(name).value.trim();
      const errors = {};
      if (!value('name')) errors.name = t.nameRequired;
      if (!value('email')) errors.email = t.emailRequired;
      else if (!EMAIL_RE.test(value('email'))) errors.email = t.emailInvalid;
      if (!input('topic').value) errors.topic = t.topicRequired;
      if (!value('message')) errors.message = t.messageRequired;
      return errors;
    };

    // The confirmation replaces the form. Focus moves to it so keyboard and
    // screen-reader users land on the result instead of on a removed button.
    const showSent = () => {
      const done = sentTemplate.content.firstElementChild.cloneNode(true);
      form.replaceWith(done);
      done.querySelector('h3').focus();
      done.querySelector('button').addEventListener('click', () => {
        done.replaceWith(form);
        input('name').focus();
      });
    };

    const onEdit = (event) => {
      const { name } = event.target;
      if (!FIELDS.includes(name)) return;
      if (event.target.hasAttribute('aria-invalid')) setError(name, '');
      if (name === 'message') updateCount();
    };
    form.addEventListener('input', onEdit);
    form.addEventListener('change', onEdit);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (submit.disabled) return;

      const errors = validate();
      FIELDS.forEach((name) => setError(name, errors[name]));
      const firstInvalid = FIELDS.find((name) => errors[name]);
      if (firstInvalid) {
        input(firstInvalid).focus();
        return;
      }

      // Bots fill every field; people never see this one. Pretend it worked.
      if (input('website').value) {
        showSent();
        return;
      }

      if (!endpoint || !key) {
        setAlert(t.unavailable);
        return;
      }

      setAlert('');
      submit.disabled = true;
      submit.textContent = t.sending;
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', apikey: key },
          body: JSON.stringify({
            name: input('name').value.trim(),
            email: input('email').value.trim(),
            topic: input('topic').value,
            message: input('message').value.trim(),
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error((body && body.error) || t.notSent);
        }
        form.reset();
        updateCount();
        showSent();
      } catch (err) {
        // fetch rejects with a TypeError when the request never reached the server.
        setAlert(err instanceof TypeError ? t.notSentConnection : err.message);
      } finally {
        submit.disabled = false;
        submit.textContent = t.submit;
      }
    });
  };

  const init = (root) => {
    syncHeader();
    root.querySelectorAll('[data-contact-form]').forEach(initContactForm);
  };

  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
